import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Box,
  ThemeProvider,
  createTheme,
} from "@mui/material";

import CHLogo from "assets/images/CH_logo.svg";
import { useAuth } from "../../hooks/useAuth";
import { QRCodeCanvas } from "qrcode.react";
import ViewBuyProduct from "components/Modal/Pawn/View/ViewBuyItems";
import ReceiptHeader from "./ReceiptHeader";
import TableProductReceipt from "./TableProductReceipt";
import ReceiptFooter from "./ReceiptFooter";
import { formatCurrency2Digit } from "utils/helpers/format";

const theme = createTheme({
  typography: {
    fontFamily: "Sarabun, sans-serif",
    fontSize: 10,
    h6: {
      fontSize: 16,
      fontWeight: "bold",
    },
  },
});

const InvoiceForm = ({ data }) => {
  console.log("display new data ", data);
  const rowsPerPage = 9;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [productData, setProductData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const { user } = useAuth();
  const calculateTotalAmount = (products) => {
    return products.reduce((total, product) => {
      return (
        total +
        (parseFloat(product.price_borrow_ || 0) *
          parseFloat(product.total_amount_borrow || 1) || 0)
      );
    }, 0);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000); // Update every second

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (data) {
      // Collect products based on the numbered suffix in data keys
      const products = [];
      const productCount = Object.keys(data).reduce((max, key) => {
        const match = key.match(/_(\d+)$/);
        if (match) {
          const number = parseInt(match[1], 10);
          return Math.max(max, number);
        }
        return max;
      }, 0);

      // Group fields for each product together
      for (let i = 1; i <= productCount; i++) {
        const product = {
          product_id: data[`product_id_${i}`],
          product_code: data[`product_code_${i}`],
          product_name: data[`product_name_${i}`],
          category_id: data[`category_id_${i}`],
          detail: data[`detail_${i}`],
          blemishes: data[`blemishes_${i}`],
          size: data[`size_${i}`],
          total_amount: data[`total_amount_${i}`],
          price: data[`price_${i}`],
          total_price: data[`total_price_${i}`],
          images: data[`images_${i}`],
          notes: data[`notes_${i}`],
          price_borrow_: data[`price_borrow_${i}`],
          total_amount_borrow: data[`total_amount_borrow_${i}`],
        };
        products.push(product);
      }

      setProductData(products);
    }
  }, [data]);

  // Function to split the productData into boxes of rowsPerPage
  const splitIntoBoxes = (data, rowsPerPage) => {
    let boxes = [];
    for (let i = 0; i < data.length; i += rowsPerPage) {
      boxes.push(data.slice(i, i + rowsPerPage));
    }
    return boxes;
  };

  // Prepare the boxes using the filtered product data
  const boxes = splitIntoBoxes(productData, rowsPerPage);
  useEffect(() => {
    // ใช้ useEffect เพื่อคำนวณและอัพเดท totalAmount ทุกครั้งที่ boxes เปลี่ยนแปลง
    const newTotalAmount = boxes.reduce((total, boxData) => {
      return total + calculateTotalAmount(boxData);
    }, 0);

    setTotalAmount(newTotalAmount); // อัพเดท totalAmount ใน state
  }, [boxes]);

  const calculateFutureDate = (currentDate, totalDays) => {
    // ตรวจสอบและแปลง totalDays ให้เป็นตัวเลข
    const daysToAdd = parseInt(totalDays, 10);

    if (isNaN(daysToAdd)) {
      console.error("Invalid totalDays value:", totalDays);
      return "Invalid date";
    }

    const futureDate = new Date(currentDate); // Clone the current date
    futureDate.setDate(futureDate.getDate() + daysToAdd); // Add total days

    // แปลงวันที่เป็นรูปแบบ dd/MM/yyyy
    return futureDate.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ตัวอย่างการใช้งาน
  // const currentDate = new Date(); // วันที่ปัจจุบัน
  // const returnDate = "5"; // จำนวนวันที่ต้องเพิ่ม
  // console.log("Future Date:", calculateFutureDate(currentDate, returnDate));

  return (
    <ThemeProvider theme={theme}>
      {boxes.map((boxData, boxIndex) => (
        <Box
          key={boxIndex}
          p={3}
          mb={2}
          border={1}
          bgcolor={"background.paper"}
          borderColor="grey.300"
          borderRadius={2}
          height={"559px"}
          width={"794px"}
          position="relative"
        >
          {/* Header Information */}
          <ReceiptHeader
            label="ใบยืมทรัพย์"
            data={data}
            date={
              data?.return_date
                ? [
                    {
                      label: "วันครบกำหนด",
                      value: calculateFutureDate(currentDate, data.return_date),
                    },
                  ]
                : []
            }
          />

          {/* Table */}
          <TableProductReceipt
            columns={[
              { label: "รายการยืมทรัพย์", key: "index" },
              {
                label: "",
                key: "product_name",
                render: (_, product) => (
                  <>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        display: "flex",
                      }}
                    >
                      {product.product_name || "-"}
                    </Typography>
                    <Typography>
                      {product.detail ? `(${product.detail})` : "-"}
                    </Typography>
                  </>
                ),
              },

              { label: "รหัส1", key: "product_code" },
              { label: "รหัส2", key: "product_code_com" },
              {
                label: "ราคา/ต่อหน่วย",
                key: "price_borrow_",
              },
              { label: "จำนวน", key: "total_amount_borrow" },
              { label: "ส่วนลด", key: "promotions" },
              {
                label: "มูลค่ารวม",
                key: "total_price",
                // Custom render function to calculate total sell price
                render: (_, product) => (
                  <Typography sx={{ fontWeight: "bold" }}>
                    {(
                      parseFloat(product.price_borrow_ || 0) *
                        parseFloat(product.total_amount_borrow || 1) -
                      parseFloat(product.promotions ?? 0)
                    ).toLocaleString() + " บาท"}
                  </Typography>
                ),
              },
            ]}
            products={boxData}
          />
          {/* Box count display (e.g., "1/3", "2/3") */}
          {/* <Typography
            style={{
              fontSize: "10px",
              position: "absolute",
              bottom: "10px",
              right: "10px",
            }}
          >
            {boxIndex + 1}/{boxes.length <= 2 ? boxes.length + 1 : boxes.length}
          </Typography> */}
          <ReceiptFooter index={boxIndex} length={boxes?.length} />
        </Box>
      ))}
      <Box
        p={3}
        mb={2}
        border={1}
        bgcolor={"background.paper"}
        borderColor="grey.300"
        borderRadius={2}
        height={"559px"}
        width={"794px"}
        position={"relative"}
      >
        {/* Header Information */}
        <ReceiptHeader
          label="ใบยืมทรัพย์"
          data={data}
          date={
            data?.return_date
              ? [
                  {
                    label: "วันครบกำหนด",
                    value: calculateFutureDate(currentDate, data.return_date),
                  },
                ]
              : []
          }
        />
        <Grid container mt={2}>
          {/* Payment Information */}
          <Grid container xs={12} sx={{ mt: 10 }}>
            <Grid
              item
              xs={6}
              container
              justifyContent="center"
              alignItems="center"
            >
              <Typography align="center" style={{ fontSize: "10px" }}>
                ชำระเงินโดย :{" "}
                {data?.payment_channel ? data.payment_channel.id : "เงินสด"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography align="right" style={{ fontSize: "10px" }}>
                    รวม
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" style={{ fontSize: "10px" }}>
                    {formatCurrency2Digit(totalAmount)} บาท
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Summary Information */}
          <Grid
            container
            xs={12}
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <Typography
                    align="right"
                    style={{ fontSize: "10px", fontWeight: "bold" }}
                  >
                    รวมจำนวนเงินรายการยืมทรัพย์ทั้งสิ้น
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    align="right"
                    style={{ fontSize: "10px", fontWeight: "bold" }}
                  >
                    {formatCurrency2Digit(totalAmount)} บาท
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box
            sx={{
              mt: 10,
              border: "1px solid #00BB9F",
              borderRadius: "12px",
              width: "100%",
            }}
          >
            <Grid container sx={{ height: "100%" }}>
              {/* Column 1 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRight: "1px solid #00BB9F",
                  padding: "10px",
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  ลูกค้า (ผู้ยืม)
                </Typography>
                <Typography align="center" style={{ fontSize: "10px" }}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>

              {/* Column 2 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRight: "1px solid #00BB9F",
                  padding: "10px",
                  height: "100%", // Ensure the column takes full height
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  เจ้าของทรัพย์ (ผู้ให้ยืม)
                </Typography>
                <Typography align="center" style={{ fontSize: "10px" }}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>

              {/* Column 3 */}
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                  height: "100%", // Ensure the column takes full height
                }}
              >
                {/* Top Divider Line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid #B0B0B0",
                    mb: 1,
                    mt: 5,
                  }}
                />
                <Typography align="center" style={{ fontSize: "12px" }}>
                  ผู้อนุมัติ
                </Typography>
                <Typography align="center" style={{ fontSize: "10px" }}>
                  {currentDate.toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Box count display (e.g., "1/3", "2/3") */}
        {/* <Typography
          style={{
            fontSize: "10px",
            position: "absolute",
            bottom: "10px",
            right: "10px",
          }}
        >
          {boxes.length <= 2
            ? boxes.length + 1 + "/" + (boxes.length + 1)
            : "3/3"}
        </Typography> */}
        <ReceiptFooter index={boxes.length} length={boxes?.length} />
      </Box>
    </ThemeProvider>
  );
};

export default InvoiceForm;
